import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { ApiCacheService } from "./api-cache.service";
import { GenericResponseDto } from "../models/generic-response.dto";
import { GetBacForSalesOrderDto } from "../models/orders/getBacForSalesOrderDto";
import { GetBCForSalesOrderDto } from "../models/orders/getBCForSalesOrderDto";
import { ShippingMethod } from "../models/shared/shippingMethod";
import { ExchangeRatesDto } from "../models/shared/exchangeRateDto";
import { GetVendorSearchResponseDto } from "../models/vendor/getVendorSearchResponseDto";
import { Address } from "../models/shared/address";
import { GenericRequestDto } from "../models/generic-request.dto";
import { ReleasedProduct } from "../models/shared/releaseProduct";
import { ReleasedProductVariants } from "../models/shared/releasedProductVariants";
import { GetInventoryPurchaseHeaderDto } from "../models/samplePO/details/getInventoryPurchaseHeaderDto";
import { GetInventoryPurchaseLineDto } from "../models/samplePO/details/getInventoryPurchaseLineDto";
import { PurchaseOrderLineSummary } from "../models/samplePO/details/samplePOSummaryDto";
import { UpdatePurchaseOrderLineResponseDto } from "../models/samplePO/details/updatePurchaseOrderLineResponseDto";
import { CreatePurchaseOrderLinesGroupResponseDto } from "../models/samplePO/details/createPurchaseOrderLinesGroupResponseDto";
import { ConfirmLineGroupRequestDto } from "../models/samplePO/details/confirmLineGroupRequestDto";
import { GetAllSamplepo } from "../models/samplePO/details/getAllSamplepo";
import { GetUserProjectDto } from "../models/samplePO/details/getUserProjectDto";
import { AddSamplePOHeaderWithLineDto } from "../models/samplePO/details/addSamplePoDto";
import { ItemsSecurity } from "../models/samplePO/details/itemsSecurity";
import { ClientSearchList } from "../models/shared/clientSearchList";
import { PurchaseOrderLine } from "../models/samplePO/details/purchaseOrderLine";
import {
  ItemAvailPhysicalDto,
  JniStoreDefaultDto,
} from "../models/inventory/inventory-lookup.models";
@Injectable({
  providedIn: "root",
})
export class SamplepoListingService {
  private samplepoManagementBaseURL =
    environment.baseUrl + "/SamplePOMangement";
  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  private vendorManagementBaseURL = environment.baseUrl + "/Vendor";
  private commanBaseURL = environment.baseUrl + "/Common";
  private userManagementBaseURL = environment.baseUrl + "/User";
  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private userSupportManagementBaseURL = environment.baseUrl + "/UserSupport";

  private readonly getAllReleaseProductUrl =
    this.samplepoManagementBaseURL + "/GetAllReleaseProduct";
  private readonly getAllShippingMethodUrl =
    this.samplepoManagementBaseURL + "/GetAllShippingMethod";
  private readonly getAllVariantsUrl =
    this.samplepoManagementBaseURL + "/GetAllVariants";
  private readonly getAllStoreDefaultsUrl =
    this.clientManagementBaseURL + "/GetAllStoreDefaults";
  private readonly samplepoLookupTtl = 8 * 60 * 60 * 1000; // 8 hours

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  getAllSamplepo(): Observable<GenericResponseDto<GetAllSamplepo[]>> {
    return this._http.get<GenericResponseDto<GetAllSamplepo[]>>(
      this.samplepoManagementBaseURL + `/GetAllSamplePO`
    );
  }

  getAllSamplePOExcel(request: GenericRequestDto): Observable<Blob> {
    return this._http.post(
      this.samplepoManagementBaseURL + `/GetAllSamplePOExcel`,
      request,
      {
        responseType: "blob",
      }
    );
  }

  getAllSamplePOPaginated(
    request: GenericRequestDto
  ): Observable<GenericResponseDto<GetAllSamplepo[]>> {
    return this._http.post<GenericResponseDto<GetAllSamplepo[]>>(
      this.samplepoManagementBaseURL + `/GetAllSamplePOPaginated`,
      request
    );
  }

  getAllShippingMethod(): Observable<GenericResponseDto<ShippingMethod[]>> {
    return this._apiCache.get<GenericResponseDto<ShippingMethod[]>>(
      this.getAllShippingMethodUrl,
      this.samplepoLookupTtl
    );
  }

  getAllCategory(): Observable<GenericResponseDto<ReleasedProduct[]>> {
    return this._apiCache.get<GenericResponseDto<ReleasedProduct[]>>(
      this.getAllReleaseProductUrl,
      this.samplepoLookupTtl
    );
  }

  getAllVariants(): Observable<GenericResponseDto<ReleasedProductVariants[]>> {
    return this._apiCache.get<GenericResponseDto<ReleasedProductVariants[]>>(
      this.getAllVariantsUrl,
      this.samplepoLookupTtl
    );
  }

  getVendorByNameAndAccountNumberFilter(
    nameAccountNumber = ""
  ): Observable<GenericResponseDto<GetVendorSearchResponseDto[]>> {
    return this._http.get<GenericResponseDto<GetVendorSearchResponseDto[]>>(
      this.vendorManagementBaseURL +
        `/SearchVendorByNameAndAcccountNumber?vendorNameOrAccountNumber=${encodeURIComponent(
          nameAccountNumber
        )}`
    );
  }

  getAllBrandingConsultant(): Observable<
    GenericResponseDto<GetUserProjectDto[]>
  > {
    return this._http.get<GenericResponseDto<GetUserProjectDto[]>>(
      this.userManagementBaseURL + `/GetAllBCUserWithProjects`
    );
  }

  getClientAndProspectByNameAndAccountNumberFilter(
    nameAccountNumber = ""
  ): Observable<GenericResponseDto<ClientSearchList[]>> {
    return this._http.get<GenericResponseDto<ClientSearchList[]>>(
      this.clientManagementBaseURL +
        `/SearchClientByNameOrAccountNumber?nameAccounNumber=${encodeURIComponent(
          nameAccountNumber
        )}`
    );
  }

  getVendorAddressByPartyNumber(
    partyNumber = "",
    accountNumber = ""
  ): Observable<GenericResponseDto<Address[]>> {
    return this._http.get<GenericResponseDto<Address[]>>(
      this.commanBaseURL +
        `/GetAddressesByPartyNumberAndAccountNumber?partyNumber=${partyNumber}&accountNumber=${accountNumber}`
    );
  }

  addSamplePoData(
    payload: AddSamplePOHeaderWithLineDto
  ): Observable<GenericResponseDto<AddSamplePOHeaderWithLineDto>> {
    return this._http.post<GenericResponseDto<AddSamplePOHeaderWithLineDto>>(
      this.samplepoManagementBaseURL + "/AddPOHeaderWithLine",
      payload
    );
  }

  deleteDraftSamplePo(payload: string): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.samplepoManagementBaseURL + "/DeleteDraftSamplePo",
      payload
    );
  }

  getCurrencyConversion(
    currency: string
  ): Observable<GenericResponseDto<ExchangeRatesDto[]>> {
    return this._http.get<GenericResponseDto<ExchangeRatesDto[]>>(
      this.d365SynchroniserBaseURL + `/GetExchangeRates?odataFilter=${currency}`
    );
  }

  getItemAvailPhysicalCost(
    odataFilter: string
  ): Observable<GenericResponseDto<ItemAvailPhysicalDto[]>> {
    return this._http.get<GenericResponseDto<ItemAvailPhysicalDto[]>>(
      this.d365SynchroniserBaseURL +
        `/GetItemAvailPhysical?odataFilter=${encodeURIComponent(odataFilter)}`
    );
  }

  getAllStoreDefaults(): Observable<GenericResponseDto<JniStoreDefaultDto[]>> {
    return this._apiCache.get<GenericResponseDto<JniStoreDefaultDto[]>>(
      this.getAllStoreDefaultsUrl,
      this.samplepoLookupTtl
    );
  }

  async getCurrencyConversionC(currency: string): Promise<number> {
    const obj = await firstValueFrom(this.getCurrencyConversion(currency)).then(
      res => {
        const sortedCurrency = res.data.sort((a, b) => {
          if (a.EndDate > b.EndDate) {
            return -1;
          }
          if (a.EndDate < b.EndDate) {
            return 1;
          }
          return 0;
        });
        const currencyConversion = sortedCurrency[0];
        const value = currencyConversion.Rate || 1;
        return value;
      }
    );
    return obj;
  }

  getBCForSalesOrder(
    payload: string
  ): Observable<GenericResponseDto<GetBCForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBCForSalesOrderDto[]>>(
      this.userManagementBaseURL + `/GetBCForSalesOrder?customerId=${payload}`
    );
  }

  getBacForSalesOrder(
    payload: string
  ): Observable<GenericResponseDto<GetBacForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBacForSalesOrderDto[]>>(
      this.userSupportManagementBaseURL +
        `/GetBacForSalesOrder?personnelNumber=${payload}`
    );
  }

  getWhoISupportTableRecord(
    email: string
  ): Observable<GenericResponseDto<GetBacForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBacForSalesOrderDto[]>>(
      this.userManagementBaseURL +
        `/WhoISupportWithProjects?email=${email}&module=samplepo`
    );
  }

  getAllWhoISupport(): Observable<GenericResponseDto<GetBCForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBCForSalesOrderDto[]>>(
      this.userSupportManagementBaseURL + `/GetAllWhoISupport`
    );
  }

  supportRoles(): Observable<GenericResponseDto<ItemsSecurity>> {
    return this._http.get<GenericResponseDto<ItemsSecurity>>(
      this.userSupportManagementBaseURL + `/SupportRoles`
    );
  }

  getInventoryPurchaseHeader(
    samplePONumber: string
  ): Observable<GenericResponseDto<GetInventoryPurchaseHeaderDto>> {
    return this._http.get<GenericResponseDto<GetInventoryPurchaseHeaderDto>>(
      this.samplepoManagementBaseURL +
        `/GetInventoryPurchaseHeader?samplePONumber=${samplePONumber}`
    );
  }

  getInventoryLine(
    id: string
  ): Observable<GenericResponseDto<GetInventoryPurchaseLineDto>> {
    return this._http.get<GenericResponseDto<GetInventoryPurchaseLineDto>>(
      this.samplepoManagementBaseURL + `/GetInventoryLine?id=${id}`
    );
  }

  getAddressUsingPostalCode(
    postalAddressD365: number
  ): Observable<GenericResponseDto<{ address: Address }>> {
    return this._http.get<GenericResponseDto<{ address: Address }>>(
      this.samplepoManagementBaseURL +
        `/GetAddressByPostalCode?code=${postalAddressD365}`
    );
  }

  getAllD365InventoryLines(
    samplePONumber: string
  ): Observable<GenericResponseDto<PurchaseOrderLineSummary[]>> {
    return this._http.get<GenericResponseDto<PurchaseOrderLineSummary[]>>(
      this.samplepoManagementBaseURL +
        `/GetAllD365InventoryLines?samplePONumber=${samplePONumber}`
    );
  }

  getAllGroupedInventoryLines(
    samplePONumber: string
  ): Observable<GenericResponseDto<PurchaseOrderLine[]>> {
    return this._http.get<GenericResponseDto<PurchaseOrderLine[]>>(
      this.samplepoManagementBaseURL +
        `/GetAllGroupedInventoryLines?samplePONumber=${samplePONumber}`
    );
  }

  addLineGroup(
    request: PurchaseOrderLine[]
  ): Observable<GenericResponseDto<PurchaseOrderLine[]>> {
    return this._http.post<GenericResponseDto<PurchaseOrderLine[]>>(
      this.samplepoManagementBaseURL + "/AddLineGroup",
      request
    );
  }

  addAndUpdateLineGroup(
    request: PurchaseOrderLine[]
  ): Observable<GenericResponseDto<UpdatePurchaseOrderLineResponseDto>> {
    return this._http.put<
      GenericResponseDto<UpdatePurchaseOrderLineResponseDto>
    >(this.samplepoManagementBaseURL + "/AddAndUpdateLineGroup", request);
  }

  confirmLineGroup(
    payload: ConfirmLineGroupRequestDto
  ): Observable<GenericResponseDto<CreatePurchaseOrderLinesGroupResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreatePurchaseOrderLinesGroupResponseDto>
    >(this.samplepoManagementBaseURL + `/ConfirmLineGroup`, payload);
  }

  getBCForProject(
    projectId: string
  ): Observable<GenericResponseDto<GetUserProjectDto>> {
    return this._http.get<GenericResponseDto<GetUserProjectDto>>(
      this.userManagementBaseURL + `/GetBCbyProject?projectId=${projectId}`
    );
  }

  getAllGroupedInventoryLinesCount(
    samplePONumber: string
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.samplepoManagementBaseURL +
        `/GetAllGroupedInventoryLinesCount?samplePONumber=${samplePONumber}`
    );
  }
}
